import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';

import { AuthenticationService } from './authentication.service';
import { LoginComponent } from '../login/login.component';
import { environment } from 'environments/environment';
import { EventService } from './event.service';
import { PublicAccessTerminalService } from './public-access-terminal.service';

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {

    // Class design adapted from:
    // https://stackoverflow.com/questions/45202208/angular-4-interceptor-retry-requests-after-token-refresh
    private refreshTokenInProgress = false;

    private tokenRefreshedSource: Subject<void> = new Subject<void>();
    private tokenRefreshed$: Observable<void> = this.tokenRefreshedSource.asObservable();

    constructor(private authenticationService: AuthenticationService,
                private eventService: EventService,
                private modalService: BsModalService,
                private publicAccessTerminalService: PublicAccessTerminalService,
                private router: Router) { }


    private addAuthHeader(request: HttpRequest<any>) {
        const authToken: string = this.authenticationService.getToken();
        if (authToken) {
            return request.clone({
                setHeaders: {
                    Authorization: `Bearer ${authToken}`
                }
            });
        }
        return request;
    }

    refreshToken(logOutReason?: string): Observable<boolean> {
        if (this.refreshTokenInProgress) {
            return new Observable(observer => {
                this.tokenRefreshed$.subscribe(() => {
                    observer.next();
                    observer.complete();
                });
            });
        } else {
            this.refreshTokenInProgress = true;
            let promise: Promise<boolean>;
            if (this.publicAccessTerminalService.isPublicAccessTerminal()) {
                promise = this.authenticationService.authenticateViaClientCertificate();
            } else {
                promise = this.showLoginPrompt(logOutReason);
            }

            return Observable.fromPromise(
                promise.then((value) => {
                    this.refreshTokenInProgress = false;
                    this.tokenRefreshedSource.next();
                    return value;
            }));
        }
    }

    private calculateLogOutReason(): string | undefined {
        let reason;
        const userIsLoggedIn = this.authenticationService.isUserLoggedIn();
        if (userIsLoggedIn) {
            const tokenShouldBeValid = this.authenticationService.isValidSessionInSessionStorage();
            reason = tokenShouldBeValid ? 'double login' : 'session expired';
         }
        return reason;
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        // Handle request
        request = this.addAuthHeader(request); // adds if user is logged in

        // Handle response
        return next.handle(request).catch((error: HttpErrorResponse) => {
            if (error.status === 401 && // TODO: remove when backend throws a normal 401
                !this.isCallWhichIgnores401(request)) { // This will prevent an endless loop if check_token returns a 401

                    const logOutReason = this.calculateLogOutReason();
                    this.authenticationService.clearSession();
                    return this.refreshToken(logOutReason)
                        .switchMap(() => {
                            request = this.addAuthHeader(request);
                            return next.handle(request);
                        })
                        .catch(() => {
                            return Observable.empty();
                        });
            }
            return Observable.throw(error);
        });
    }

    private isCallWhichIgnores401(request: HttpRequest<any>): boolean {
        return request.url.indexOf(environment.security.check_token_uri) !== -1 ||
               request.url.indexOf(environment.security.revoke_token_uri) !== -1;
    }

    private showLoginPrompt(logOutReason: string): Promise<boolean> {
        // not logged in so route to 'login' and return false
        const bsModalRef = this.modalService.show(LoginComponent, {class: 'modal-lg', backdrop: 'static'});
        if (logOutReason) {
            setTimeout(() => { bsModalRef.content.logOutReason = logOutReason; });
        }
        return new Promise<boolean>((resolve, reject) => {
            bsModalRef.content.loginCompleted$.subscribe(value => {
                resolve(value);
                bsModalRef.hide();
            });

            this.eventService.sessionTimedOut$.subscribe(() => {
                resolve(true);
                bsModalRef.hide();
            });
         });
    }
}



// WEBPACK FOOTER //
// /home/jenkins/workspace/pipeline-ciprs-ui-final/project/src/app/shared/authentication-interceptor.ts